import React from 'react';
import s from './Notification.scss';
import Grid from '../Grid/Grid';
import Column from '../Column/Column';
import DeprecatedCheckbox from '../DeprecatedCheckbox/DeprecatedCheckbox';
import Icon from '../Icon/Icon';
import IconV2 from '../IconV2/IconV2';

export type NotificationProps = {
  checkboxLabel?: string;
  checkboxOnClick?: (
    ev: React.MouseEvent<HTMLInputElement> | React.ChangeEvent<HTMLInputElement>
  ) => Promise<void> | void;
  closeable?: boolean;
  color?: NotificationColors;
  exclamationPoint?: boolean;
  imageClassName?: string;
  imageSrc?: string;
  justification?: NotificationJustification;
  onClose?: () => void;
  text?: string;
};

export type NotificationState = {
  open: boolean;
};
export type NotificationColors = 'red' | 'blue' | 'gray';
export type NotificationJustification = 'center' | 'left';

let instance: number;

/**
 * The component for creating blocks of text that draw the user's attention to
 * important bits of information.
 *
 * ## Examples
 * ```html
 *   <Notification text="Look at this" color="blue" exclamationPoint/>
 * ```
 **/
export default class Notification extends React.Component<
  NotificationProps,
  NotificationState
> {
  el!: HTMLButtonElement | null;
  instance: number;

  constructor(props: NotificationProps) {
    super(props);
    this.state = {
      open: true,
    };
    instance += 1;
    this.instance = instance;
    this.closeNotification = this.closeNotification.bind(this);
  }

  public static displayName = 'Notification';

  componentDidMount() {
    if (this.el) this.el.focus();
  }

  closeNotification() {
    this.setState(
      {
        open: false,
      },
      () => {
        if (this.props.onClose && typeof this.props.onClose === 'function') {
          this.props.onClose();
        }
      }
    );
  }

  render() {
    const {
      closeable,
      color = 'gray',
      exclamationPoint,
      justification = 'left',
      text,
      checkboxLabel,
      checkboxOnClick,
      imageSrc,
      imageClassName,
    } = this.props;
    const { open } = this.state;

    let containerClassNames = `${s[`${color}Notification`]} notification`;
    if (!open) {
      containerClassNames = '';
    }

    const textClassName = `${s.text} ${s[`${justification}Align`]}`;

    if (open) {
      return (
        <div
          className={containerClassNames}
          role="dialog"
          aria-describedby={`rvn-note-${this.instance}`}
          aria-label="Alert!"
        >
          <Grid bleed>
            <Column wrap="no-wrap">
              {exclamationPoint && (
                <div className={s.exclamationColumn}>
                  <span className={s.exclamation}>
                    <Icon icon="alertIconBlack"></Icon>
                  </span>
                </div>
              )}
              <div className={textClassName} id={`rvn-note-${this.instance}`}>
                {text}
              </div>
              {checkboxLabel && checkboxOnClick && (
                <DeprecatedCheckbox
                  name={`${checkboxLabel}name`}
                  label={`${checkboxLabel}`}
                  onForm={false}
                  containerClassName={`${s.checkbox}`}
                  labelClassName={`s[${color}-notification] ${s.checkboxLabel}`}
                  onClick={checkboxOnClick}
                />
              )}
            </Column>
            {imageSrc && (
              <img src={imageSrc} className={imageClassName} alt="" />
            )}
            {closeable && (
              <Column span={{ fixed: '1' }}>
                <div className={s.closeableColumn}>
                  <button
                    type="button"
                    onClick={this.closeNotification}
                    className={s.closeable}
                    // grandfathered in
                    // eslint-disable-next-line react/jsx-no-bind
                    ref={(el) => {
                      this.el = el;
                    }}
                    aria-label="Dismiss Alert"
                  >
                    <IconV2 icon="close" size="small"></IconV2>
                  </button>
                </div>
              </Column>
            )}
          </Grid>
        </div>
      );
    }

    return null;
  }
}
